import React from 'react';
import styled, { css } from 'styled-components';
import { noop } from 'lodash-es';
// DownArrow is an untyped .js import — resolves as any
import DownArrow from 'icons/down-arrow';

import { gridUnits } from '../utils';

interface ContainerProps {
  borderRadius?: string;
  disabled?: boolean;
  disabledColor?: string;
  color?: string;
}

const Container = styled.div<ContainerProps>`
  border-radius: ${(props) => props.borderRadius || '4px'};
  display: inline-flex;
  overflow: hidden;
  color: ${(props) => (props.disabled ? props.disabledColor : props.color)};
  transition: color 0.2s ease;
`;

interface ButtonProps {
  disabled?: boolean;
  disabledColor: string;
  disabledBgColor: string;
  size?: string;
  style?: { bgColor: string; primaryColor: string };
  onClick?: () => void;
}

const Button = styled(({ disabled, disabledColor, disabledBgColor, ...props }: ButtonProps) => (
  <DownArrow {...props} />
)).attrs<ButtonProps>((props) => ({
  style: {
    bgColor: props.disabled ? props.disabledColor : 'currentColor',
    primaryColor: props.disabled ? props.disabledBgColor : 'white',
  },
  size: gridUnits(1),
}))`
  color: inherit;
  background-color: ${(props) => (props.disabled ? props.disabledColor : 'currentColor')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  cursor: pointer;
  transition: fill 0.2s ease;

  &:first-of-type {
    transform: rotateZ(90deg);
  }

  &:last-of-type {
    transform: rotateZ(-90deg);
  }
`;

const PageLabel = styled.p`
  margin: 0;
`;

interface LabelContainerProps {
  width?: string;
  disabled?: boolean;
  disabledLeft?: boolean;
  disabledRight?: boolean;
  disabledColor?: string;
  disabledBgColor?: string;
  color?: string;
}

const borderGradient = css<LabelContainerProps>`
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-image: linear-gradient(
    to right,
    ${(props) => (props.disabledLeft ? props.disabledColor : props.color)},
    ${(props) => (props.disabledRight ? props.disabledColor : props.color)} 100%
  );
  transition: background-color 0.2s ease;
`;

const LabelContainer = styled.div<LabelContainerProps>`
  min-width: ${(props) => props.width || gridUnits(1.5)};
  padding: 0 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: background-color 0.2s ease;

  &:after {
    bottom: 0;
    ${borderGradient};
  }

  &:before {
    top: 0;
    ${borderGradient};
  }

  ${(props) => props.disabled && `background-color: ${props.disabledBgColor}`};
`;

interface FjPaginatorProps {
  className?: string;
  onClickLeft?: () => void;
  onClickRight?: () => void;
  disabledLeft?: boolean;
  disabledRight?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  width?: string;
  color?: string;
  disabledColor?: string;
  disabledBgColor?: string;
}

const FjPaginator = ({
  className,
  onClickLeft = noop,
  onClickRight = noop,
  disabledLeft,
  disabledRight,
  disabled: disabledProp,
  label,
  width,
  color = '#000000',
  disabledColor = '#B5BEBB',
  disabledBgColor = '#E9EBEA',
}: FjPaginatorProps) => {
  const disabled = disabledProp || (disabledLeft && disabledRight);
  const containerProps = {
    className,
    color,
    disabled,
    disabledColor,
  };
  const buttonProps = {
    disabledColor,
    disabledBgColor,
  };
  const labelContainerProps = {
    width,
    disabled,
    disabledLeft,
    disabledRight,
    disabledColor,
    disabledBgColor,
    color,
  };
  return (
    <Container {...containerProps}>
      <Button disabled={disabledLeft || disabled} onClick={onClickLeft} {...buttonProps} />
      <LabelContainer {...labelContainerProps}>
        <PageLabel>{label}</PageLabel>
      </LabelContainer>
      <Button disabled={disabledRight || disabled} onClick={onClickRight} {...buttonProps} />
    </Container>
  );
};

export default FjPaginator;
